import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';

import { analytics } from 'analytics';
import Button from 'ui/Button';
import Field from 'ui/Field';
import Input, { InputGroup } from 'ui/Input';
import { VStack } from 'ui/Layout';
import PasswordInput from 'ui/PasswordInput';

import { useApiContext } from '../../contexts/apiContext';
import { useFormErrors } from '../../utils/error';
import { useCurrentUser } from '../../utils/user';
import { emailRegexp } from '../../utils/validation';

type FormInput = {
  email: string;
  password: string;
};

type Props = {
  onSuccess?: () => void;
};

const SignInWithEmailForm: React.FC<Props> = ({ onSuccess }) => {
  const { refetchUser } = useCurrentUser();
  const { api } = useApiContext();

  const form = useForm<FormInput>({
    defaultValues: {
      email: '',
      password: '',
    },
  });
  const { showFormErrors } = useFormErrors(form);

  const { mutateAsync: signIn, isLoading } = useMutation(async () => {
    try {
      const { email, password } = form.getValues();
      await api().currentUser.signInViaEmail({
        loginRequest: { email, password },
      });
      await refetchUser();
      onSuccess?.();
    } catch (error: any) {
      // If the error doesn't come from form data, report it to Sentry
      if (!showFormErrors(error.response?.data)) {
        analytics.reportError('Invalid sign in credentials', { error });
      }
    }
  });

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    // If the form is invalid, we use our own form error handling
    // to make sure all non-field errors are captured correctly
    form.handleSubmit(() => signIn(), showFormErrors)();
  };

  return (
    <form onSubmit={handleSubmit}>
      <VStack alignItems="stretch" gap={4}>
        <Field
          label="Email"
          invalid={!!form.formState.errors.email}
          errorText={form.formState.errors.email?.message}
        >
          <InputGroup>
            <Input
              {...form.register('email', {
                required: 'Please enter your email',
                pattern: {
                  value: emailRegexp,
                  message: 'Please enter a valid email address',
                },
              })}
              autoFocus
              placeholder="john@business.com"
            />
          </InputGroup>
        </Field>
        <Field
          label="Password"
          invalid={!!form.formState.errors.password}
          errorText={form.formState.errors.password?.message}
        >
          <PasswordInput
            {...form.register('password', {
              required: 'Please enter a password',
            })}
          />
        </Field>
        <Button
          type="submit"
          variant="primary-solid"
          loading={isLoading}
          my={2}
        >
          Sign in
        </Button>
      </VStack>
    </form>
  );
};

export default SignInWithEmailForm;
